import { TransitionDto } from "@repo/api/transition";
import { Audio } from "@remotion/media";
import { interpolate, Sequence, useVideoConfig } from "remotion";
import { msToFrames } from "../../utils/timing.ts";
import { IMAGE_TRANSITION_SOUND_PEAK_AT } from "../ImageTransitionBuilder/ImageTransitionBuilder.ts";
import {
  getTransitionSoundVolume,
  TRANSITION_SOUNDS,
} from "./transition-sounds.ts";

type Props = {
  transition: TransitionDto;
  transitionStartFrame: number;
  transitionDurationInFrames: number;
  endFrame: number;
};

const FADE_OUT_MS = 250;

// Starts early enough that the sound's peak lands on the transition's peak.
// Must sit outside the clips' sequences, which would cut the lead-in and the tail.
export default function TransitionSound({
  transition,
  transitionStartFrame,
  transitionDurationInFrames,
  endFrame,
}: Props) {
  const { fps } = useVideoConfig();

  if (!transition.sound) return null;

  const sound = TRANSITION_SOUNDS[transition.sound];
  let from: number;
  let trimBefore: number;
  let soundEndFrame = endFrame;
  if ("peakMs" in sound) {
    const peakAt = IMAGE_TRANSITION_SOUND_PEAK_AT[transition.kind] ?? 0.5;
    from = Math.round(
      transitionStartFrame +
        transitionDurationInFrames * peakAt -
        msToFrames(sound.peakMs, fps),
    );
    trimBefore = Math.max(0, -from);
  } else {
    from = transitionStartFrame;
    trimBefore = msToFrames(sound.duringTransitionFromMs, fps);
    soundEndFrame = Math.min(
      endFrame,
      transitionStartFrame + transitionDurationInFrames,
    );
  }

  const startFrame = Math.max(0, from);
  const durationInFrames = Math.max(1, soundEndFrame - startFrame);
  const fadeOutFrames = Math.min(
    msToFrames(FADE_OUT_MS, fps),
    Math.ceil(durationInFrames / 3),
  );
  const volume =
    getTransitionSoundVolume(transition.sound) * (transition.volume ?? 1);

  return (
    <Sequence
      from={startFrame}
      durationInFrames={durationInFrames}
      name="Transition sound"
      premountFor={msToFrames(1000, fps)}
    >
      <Audio
        src={sound.src}
        trimBefore={trimBefore}
        volume={(frame: number) =>
          volume *
          interpolate(
            frame,
            [durationInFrames - fadeOutFrames, durationInFrames],
            [1, 0],
            { extrapolateLeft: "clamp", extrapolateRight: "clamp" },
          )
        }
        pauseWhenBuffering={true}
      />
    </Sequence>
  );
}
